import { Component, useEffect, useRef, useState } from 'react';
import './App.css';
import {
  flushTelemetry,
  trackError,
  trackEvent,
} from './app/telemetry';
import GameLibrary from './shared/library/GameLibrary';
import {
  getVisibleGames,
  readSelectedPlayerIcons,
  writeSelectedPlayerIcon,
} from './shared/library/gameCatalog';

export class ArcadeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    trackError(error, {
      componentStack: errorInfo?.componentStack,
      context: 'react_error_boundary',
      gameId: this.props.gameId,
      releaseState: this.props.releaseState,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <main className="game-app">
          <section className="game-shell" aria-labelledby="app-title">
            <div className="game-overlay" role="alert">
              <div className="overlay-panel">
                <p>Game Error</p>
                <strong>Something broke</strong>
                <button type="button" onClick={this.props.onReturnToLibrary}>
                  Game Menu
                </button>
              </div>
            </div>
          </section>
        </main>
      );
    }

    return this.props.children;
  }
}

function App() {
  const visibleGames = getVisibleGames();
  const appLoadedRef = useRef(false);
  const [activeGameId, setActiveGameId] = useState(null);
  const [selectedPlayerIcons, setSelectedPlayerIcons] = useState(() =>
    readSelectedPlayerIcons(window.localStorage, getVisibleGames())
  );
  const activeGame = visibleGames.find((game) => game.id === activeGameId && game.component) ?? null;

  useEffect(() => {
    if (!appLoadedRef.current) {
      appLoadedRef.current = true;
      trackEvent('app.loaded', {
        visibleGames: visibleGames.length,
      });
    }

    const onWindowError = (event) => {
      trackError(event.error ?? event.message, {
        context: 'window.error',
      });
    };
    const onUnhandledRejection = (event) => {
      trackError(event.reason, {
        context: 'window.unhandledrejection',
      });
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('pagehide', flushTelemetry);

    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('pagehide', flushTelemetry);
    };
  }, [visibleGames.length]);

  useEffect(() => {
    if (!activeGame) {
      trackEvent('library.viewed', {
        visibleGames: visibleGames.length,
      });
    }
  }, [activeGame, visibleGames.length]);

  const selectPlayerIcon = (gameId, iconId) => {
    writeSelectedPlayerIcon(gameId, iconId, window.localStorage);
    trackEvent('playable_icon.selected', {
      gameId,
      iconId,
    });
    setSelectedPlayerIcons((current) => ({
      ...current,
      [gameId]: iconId,
    }));
  };

  const selectGame = (gameId) => {
    const selectedGame = visibleGames.find((game) => game.id === gameId);

    if (selectedGame?.component) {
      trackEvent('game.started', {
        gameId: selectedGame.id,
        releaseState: selectedGame.releaseState,
        selectedPlayableIcon: selectedPlayerIcons[selectedGame.id],
      });
    }

    setActiveGameId(gameId);
  };

  const returnToLibrary = () => {
    if (activeGame) {
      trackEvent('game.returned_to_library', {
        gameId: activeGame.id,
        releaseState: activeGame.releaseState,
      });
    }

    setActiveGameId(null);
  };

  if (activeGame) {
    const GameComponent = activeGame.component;

    return (
      <ArcadeErrorBoundary
        gameId={activeGame.id}
        key={activeGame.id}
        onReturnToLibrary={returnToLibrary}
        releaseState={activeGame.releaseState}
      >
        <GameComponent
          definition={activeGame.definition}
          onReturnToLibrary={returnToLibrary}
          selectedPlayableIcon={selectedPlayerIcons[activeGame.id]}
        />
      </ArcadeErrorBoundary>
    );
  }

  return (
    <GameLibrary
      games={visibleGames}
      onSelectGame={selectGame}
      onSelectPlayerIcon={selectPlayerIcon}
      selectedPlayerIcons={selectedPlayerIcons}
    />
  );
}

export default App;
